import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { ServiceAreas } from "@/components/site/ServiceAreas";
import { Phone } from "lucide-react";

const faqs = [
  {
    q: "Do you deliver luxury cars in Vancouver?",
    a: "Yes. Avolus is delivery-first: we deliver to your door, hotel, residence, or YVR airport, complimentary within Vancouver, and collect the car when you're done.",
  },
  {
    q: "Do you deliver to YVR airport?",
    a: "Yes. YVR airport is included in our complimentary delivery zone, including after-hours arrivals.",
  },
  {
    q: "Do you deliver throughout Greater Vancouver and Whistler?",
    a: "Yes. We deliver throughout Greater Vancouver (Richmond, Burnaby, North and West Vancouver, Surrey, Coquitlam, Langley, Abbotsford) as well as Squamish and Whistler. Outside of Vancouver and YVR airport, delivery is subject to a quick call to confirm pricing.",
  },
  {
    q: "Is delivery free?",
    a: "Delivery is complimentary within Vancouver and to YVR airport. Everywhere else across Greater Vancouver and Whistler is by arrangement. Give us a call and we'll quote delivery for your exact pickup point.",
  },
];

export const Route = createFileRoute("/delivery")({
  component: DeliveryPage,
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Luxury Car Delivery in Vancouver | Avolus Cars" },
      {
        name: "description",
        content:
          "Avolus delivers luxury cars free within Vancouver, including YVR airport and downtown. Mercedes, Cadillac, BMW delivered and collected. Book today.",
      },
      { property: "og:title", content: "Luxury Car Delivery in Vancouver | Avolus Cars" },
      {
        property: "og:description",
        content:
          "Avolus delivers luxury cars free within Vancouver, including YVR airport and downtown. Mercedes, Cadillac, BMW delivered and collected. Book today.",
      },
      { property: "og:url", content: "https://avoluscars.com/delivery" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/delivery" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://avoluscars.com/" },
            { "@type": "ListItem", position: 2, name: "Delivery", item: "https://avoluscars.com/delivery" },
          ],
        }),
      },
    ],
  }),
});

function DeliveryPage() {
  return (
    <Layout>
      <ServiceAreas isCurrentPage />

      <section className="py-16 md:py-20 border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Delivery</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-4xl leading-[1.05]">
              Luxury Cars, Delivered to You
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
              Avolus is a delivery-first luxury car rental serving all of Greater Vancouver,
              including Whistler. We bring the car to you, fully detailed and fueled, and collect
              it when you're done. Delivery is complimentary within Vancouver and to YVR airport;
              everywhere else is subject to a quick call to confirm pricing.
            </p>
            <p className="mt-4 text-sm text-muted-foreground max-w-3xl">
              Avolus Cars is a delivery-first luxury car rental in Vancouver, BC.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="/book"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 transition-colors"
              >
                Book Now
              </a>
              <a
                href="tel:+17783798881"
                className="md:hidden inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                <Phone size={14} /> +1-778-379-8881
              </a>
              <a
                href="/contact"
                className="hidden md:inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Contact Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-border">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Coverage</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground">Where we deliver</h2>
            <ul className="mt-8 space-y-4 text-foreground/85 leading-relaxed list-disc pl-5">
              <li>
                <strong>Free delivery</strong> within Vancouver and to YVR airport, including
                downtown, Coal Harbour, the Convention Centre, and West End hotels.
              </li>
              <li>
                <strong>Greater Vancouver &amp; Whistler, by arrangement.</strong> Richmond,
                Burnaby, North &amp; West Vancouver, Surrey, Coquitlam, Langley, Abbotsford,
                Squamish, and Whistler. Delivery outside Vancouver and YVR airport is subject to a
                call to determine pricing.
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-border bg-muted/40">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Process</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground">How delivery works</h2>
            <ol className="mt-8 space-y-4 text-foreground/85 leading-relaxed list-decimal pl-5">
              <li>Book your vehicle and dates.</li>
              <li>Tell us where and when: home, hotel, office, or YVR.</li>
              <li>We deliver the car detailed and fueled, walk you through it, and hand over the keys.</li>
              <li>When you're done, we collect it from wherever you are.</li>
            </ol>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-border">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Why Delivery-First</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground">No counter, no shuttle, no lot</h2>
            <div className="mt-8 space-y-6 text-foreground/85 leading-relaxed">
              <p>
                Avolus has no physical pickup location, and that's on purpose. Every vehicle is
                delivered to you, which means the rental starts where you already are instead of
                in a queue across town. For a wedding, that might be the bridal suite. For a
                business trip, it's the hotel lobby or the arrivals curb. For a weekend away,
                it's your own driveway.
              </p>
              <p>
                It also means you get the car in the condition it should be: freshly detailed,
                fuelled and ready to drive, handed over by someone who knows the vehicle and can
                answer your questions. When the rental ends, there's no rushing back to return
                it. We come to you again and collect it, so the last hour of your trip is yours,
                not spent hunting for a drop-off.
              </p>
              <p>
                Planning ahead? Tell us both addresses if your pickup and return locations are
                different, for example delivery at YVR and collection from a downtown hotel. We
                plan the timing around your day.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-border">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Areas</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground">Delivery across Greater Vancouver</h2>
            <div className="mt-8 space-y-8 text-foreground/85 leading-relaxed">
              <div>
                <h3 className="font-display text-2xl text-foreground">Vancouver</h3>
                <p className="mt-3">
                  Delivery anywhere in the City of Vancouver is complimentary. That includes
                  downtown condos and offices, Coal Harbour, Yaletown, the West End, the
                  Convention Centre and the hotels along the waterfront. Most guests simply give
                  us the address and a time, and the car is waiting when they walk outside.
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl text-foreground">YVR Airport</h3>
                <p className="mt-3">
                  YVR is part of our complimentary delivery zone, including after-hours arrivals.
                  We track your flight, so if you land early or late the handover moves with you.
                  You skip the rental counter and the shuttle entirely. See our{" "}
                  <a href="/yvr-airport-car-rental" className="underline underline-offset-4">YVR airport car rental</a>{" "}
                  page for the details of an airport handover.
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl text-foreground">Richmond and Burnaby</h3>
                <p className="mt-3">
                  Richmond and Burnaby are close by and delivered regularly. Delivery here is by
                  arrangement: call us with your address and we'll confirm pricing for your exact
                  location before you book.
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl text-foreground">North and West Vancouver</h3>
                <p className="mt-3">
                  Across the bridges to North Vancouver and West Vancouver, delivery is also by
                  arrangement. It's a popular choice for guests heading up the Sea-to-Sky, since
                  the car arrives ready for the drive.
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl text-foreground">Whistler, by arrangement</h3>
                <p className="mt-3">
                  We also deliver to Squamish and Whistler. Because of the distance, Whistler
                  delivery is arranged individually. Give us a call with your dates and pickup
                  point and we'll quote it for you.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-border bg-muted/40">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Timing &amp; Handover</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground">What to expect on the day</h2>
            <div className="mt-8 space-y-6 text-foreground/85 leading-relaxed">
              <p>
                <strong className="text-foreground">Timing.</strong> Our concierge is available
                seven days a week from 8:00 AM to
                8:00 PM. Private after-hours delivery is available by appointment, so a late
                flight or an early start isn't a problem. Each rental day runs a full 24 hours
                from the moment you take the keys.
              </p>
              <p>
                <strong className="text-foreground">The handover.</strong> The car arrives
                professionally detailed and fuelled, with no rental stickers or branding. We walk
                you through the vehicle, check your driver's licence and second piece of ID, and
                confirm the details of your booking. The $2,000 refundable security deposit is
                pre-authorized on a major credit card in the renter's name at delivery.
              </p>
              <p>
                <strong className="text-foreground">During your rental.</strong> Short-term
                rentals include 150 km per day, and every rental includes LDW at $60/day. If
                anything comes up, our concierge is a phone call away.
              </p>
              <p>
                <strong className="text-foreground">Pickup.</strong> When you're done, we collect
                the car from wherever you are: your home, hotel, office or the airport. Just
                return it at the same fuel level you received it. After the car passes
                inspection, the deposit hold is released.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-border">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground">Delivery FAQs</h2>
            <div className="mt-10 space-y-8">
              {faqs.map((f) => (
                <div key={f.q}>
                  <h3 className="font-display text-xl text-foreground">{f.q}</h3>
                  <p className="mt-3 text-foreground/80 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

    </Layout>
  );
}
